import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const programItems = [
    { name: "Becoming Intensive", path: "/programs/becoming-intensive" },
    { name: "Workbook", path: "/programs/workbook" },
    { name: "Identity Reset", path: "/programs/identity-reset" },
  ];

  const isProgramActive = location.pathname.startsWith("/programs");

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-[#4A0E26] text-white text-[11px] font-bold py-1.5 px-4 text-center uppercase tracking-widest">
        <Link to="/programs/becoming-intensive" className="hover:underline">
          The next Intensive begins January 11th — Sign up now!
        </Link>
      </div>

      {/* Main Navbar */}
      <header className="sticky top-0 z-50 bg-[#FAF6F0]/90 backdrop-blur-md border-b border-[#E8DEC8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          {/* Wordmark */}
          <Link to="/" className="flex flex-col group">
            <span className="font-serif text-2xl md:text-3xl font-normal tracking-wide text-[#380C1E] group-hover:text-[#9E1B50] transition-colors">
              IESHIA <span className="italic font-light">DANIELLE</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/about"
              className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-[#9E1B50] ${
                location.pathname === "/about"
                  ? "text-[#9E1B50] font-bold"
                  : "text-[#4A0E26]"
              }`}
            >
              About
            </Link>

            {/* Programs Dropdown */}
            <div className="relative group">
              <Link
                to="/programs"
                className={`text-xs font-semibold tracking-wider uppercase transition-colors flex items-center gap-1 hover:text-[#9E1B50] ${
                  isProgramActive
                    ? "text-[#9E1B50] font-bold"
                    : "text-[#4A0E26]"
                }`}
              >
                Programs
                <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-[#FAF6F0] border border-[#E8DEC8] rounded-xl shadow-lg p-2 min-w-[200px]">
                  {programItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#4A0E26] hover:text-[#9E1B50] hover:bg-[#EFE6D2]/60 rounded-lg transition-colors cursor-pointer"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/shop"
              className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-[#9E1B50] ${
                location.pathname === "/shop"
                  ? "text-[#9E1B50] font-bold"
                  : "text-[#4A0E26]"
              }`}
            >
              Shop
            </Link>

            <Link
              to="/book"
              className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-[#9E1B50] ${
                location.pathname === "/book"
                  ? "text-[#9E1B50] font-bold"
                  : "text-[#4A0E26]"
              }`}
            >
              Booking
            </Link>

            <Link
              to="/podcast"
              className={`text-xs font-semibold tracking-wider uppercase transition-colors hover:text-[#9E1B50] ${
                location.pathname === "/podcast"
                  ? "text-[#9E1B50] font-bold"
                  : "text-[#4A0E26]"
              }`}
            >
              Podcast
            </Link>
          </nav>

          {/* Header Button */}
          <div className="hidden md:block">
            <Link
              to="/programs/becoming-intensive"
              className="bg-[#4A0E26] hover:bg-[#330A1F] text-white text-[11px] font-bold uppercase tracking-wider px-6 py-3 rounded-full transition-all shadow-sm inline-block hover:-translate-y-0.5"
            >
              Join the Intensive
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#4A0E26] p-2 focus:outline-none"
              aria-label="Toggle Navigation Menu"
            >
              {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#FAF6F0] border-b border-[#E8DEC8] px-6 pt-4 pb-6 space-y-4">
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-semibold text-[#4A0E26] hover:text-[#9E1B50] py-2 border-b border-[#E8DEC8]"
            >
              About
            </Link>

            <div className="py-2 border-b border-[#E8DEC8] space-y-2">
              <Link
                to="/programs"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-xs font-bold text-[#9E1B50] uppercase tracking-wider"
              >
                Programs
              </Link>
              <div className="pl-4 space-y-2">
                {programItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-sm font-medium text-[#4A0E26] hover:text-[#9E1B50]"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/shop"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-semibold text-[#4A0E26] hover:text-[#9E1B50] py-2 border-b border-[#E8DEC8]"
            >
              Shop
            </Link>

            <Link
              to="/book"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-semibold text-[#4A0E26] hover:text-[#9E1B50] py-2 border-b border-[#E8DEC8]"
            >
              Booking
            </Link>

            <Link
              to="/podcast"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-semibold text-[#4A0E26] hover:text-[#9E1B50] py-2 border-b border-[#E8DEC8]"
            >
              Podcast
            </Link>

            <div className="pt-2">
              <Link
                to="/programs/becoming-intensive"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center w-full bg-[#4A0E26] text-white font-bold uppercase text-xs tracking-wider py-3 rounded-full"
              >
                Join the Intensive
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
